import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import debounce from "lodash/debounce";
import qs from "qs";
import uniqBy from "lodash/uniqBy";
import isEqual from "lodash/isEqual";
import castArray from "lodash/castArray";
import axios from "../../axios";
import { usePrevious } from "../../utils/hooks/usePrevious";
import i18n from "../../i18n";
import { prepareInitialOptions } from "./methods";

const SCROLL_BOTTOM_THRESHOLD = 8;

const parseOptionsResponse = (res) => {
  const pager = res.data.data.pager;
  const data = res.data.data.pager ? res.data.data.data : res.data.data;
  return { pager: pager || {}, data: data || [] };
};

/**
 * Manages all option fetching, lazy loading, search, and reactive state updates
 * for ResourceSelectLazyLoading.
 *
 * @param {object} params
 * @param {string}   params.apiRoute
 * @param {object}   params.customParams
 * @param {*}        params.initialValue
 * @param {Array}    params.staticOptions
 * @param {boolean}  params.overwritePreviousOptions
 * @param {number}   params.perPage
 * @param {number}   params.page
 * @param {Function} params.textAccessor
 * @param {Function} params.valueAccessor
 * @param {Function} params.transformer
 * @param {Function} params.onGetOptions
 * @param {Function} params.onLanguageChange
 * @param {Function} params.onDropdownVisibleChange
 * @param {string[]} params.searchColumns
 * @param {string[]} params.searchIn
 * @param {boolean}  params.refetchOptions
 * @param {boolean}  params.requestOnMount
 * @param {*}        params.value
 * @returns {{ options, loading, serverSearch, onVisibleChange, onScroll, debouncedOnSearch }}
 */
export const useSelectOptions = ({
  apiRoute,
  customParams,
  initialValue,
  staticOptions,
  overwritePreviousOptions,
  perPage,
  page,
  textAccessor,
  valueAccessor,
  transformer,
  onGetOptions,
  onLanguageChange,
  onDropdownVisibleChange,
  searchColumns,
  searchIn,
  refetchOptions,
  requestOnMount,
  value
}) => {
  const { language } = i18n;
  const [options, setOptions] = useState(
    prepareInitialOptions(initialValue, textAccessor, valueAccessor, staticOptions)
  );
  const [params, setParams] = useState({ per_page: perPage, page });
  const [firstRequestMade, setFirstRequestMade] = useState(false);
  const [loading, setLoading] = useState({ initial: false, lazy: false, onMount: false });
  // Bumped every time a request replaces the list (search, refetch). A response is stale only
  // if the list was replaced after its request went out: two pages loading at once must both
  // land, but a page requested before a search must not reappear on top of its results.
  const listGenerationRef = useRef(0);
  const activeSearchRef = useRef("");
  // Lets an in-flight response merge into the list as it is when the response lands, rather
  // than into the copy captured when the request went out.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Searching over the API needs the whole data set, not just the loaded page. It is skipped
  // only for option lists that never come from the API, and for the ones filtering locally
  // over several fields (searchIn), which the API cannot reproduce from a single value.
  const serverSearch = Boolean(apiRoute) && (Boolean(searchColumns?.length) || !searchIn?.length);

  const setLoadingField = (key, val) => setLoading((prev) => ({ ...prev, [key]: val }));

  // Refetch options when refetchOptions prop flips
  const prevRefetchOptions = usePrevious(refetchOptions);
  useEffect(() => {
    if (refetchOptions && refetchOptions !== prevRefetchOptions) {
      getOptions({
        onStart: () => setLoadingField("onMount", true),
        onSuccess: () => setLoadingField("onMount", false),
        onFailed: () => setLoadingField("onMount", false),
        removeExistingOptions: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchOptions]);

  // Update option labels when app language changes
  const prevLanguage = usePrevious(language);
  useEffect(() => {
    if (onLanguageChange && language !== prevLanguage) {
      setOptions(onLanguageChange(options));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Prepend / merge initialValue into options list when it changes
  const previousInitialValue = usePrevious(initialValue);
  useEffect(() => {
    if (!isEqual(initialValue, previousInitialValue)) {
      setOptions(prepareInitialOptions(initialValue, textAccessor, valueAccessor, options));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  // Merge updated staticOptions into the list
  const previousStaticOptions = usePrevious(staticOptions);
  useEffect(() => {
    if (!isEqual(staticOptions, previousStaticOptions)) {
      setOptions(
        prepareInitialOptions(
          initialValue,
          textAccessor,
          valueAccessor,
          overwritePreviousOptions ? staticOptions : [...staticOptions, ...options]
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticOptions]);

  const getOptions = useCallback(
    (p = {}) => {
      if (!apiRoute) return;

      const {
        reqParams = {},
        onStart = () => {},
        onSuccess = () => {},
        onFailed = () => {},
        removeExistingOptions
      } = p;

      onStart();

      if (removeExistingOptions) listGenerationRef.current += 1;
      const generation = listGenerationRef.current;

      axios
        .get(apiRoute, {
          params: { light: 1, ...reqParams, ...customParams },
          paramsSerializer: (preparedParams) => qs.stringify(preparedParams, { arrayFormat: "brackets" })
        })
        .then(
          (res) => {
            if (generation !== listGenerationRef.current) return;

            const { pager, data } = parseOptionsResponse(res);
            // When the list is replaced, keep the selected option(s) so their label stays
            // resolvable even if they fall outside the new result set.
            const selectedValues = new Set(castArray(value ?? []).filter((val) => val !== undefined && val !== null));
            const keptOptions = removeExistingOptions
              ? optionsRef.current.filter((option) => selectedValues.has(valueAccessor(option)))
              : optionsRef.current;
            const preparedOptions = [...keptOptions, ...transformer([...data])].map((option) => ({
              ...option,
              text: textAccessor(option),
              value: valueAccessor(option)
            }));
            const uniqueOptions = uniqBy(preparedOptions, (option) => valueAccessor(option));
            setOptions(uniqueOptions);
            onSuccess(data || [], pager || {});
            onGetOptions?.(uniqueOptions, value);
            if (!firstRequestMade) setFirstRequestMade(true);
          },
          (error) => onFailed(error)
        );
    },
    // Reading the option list through a ref keeps this callback stable across list changes,
    // so handlers built on it cannot go stale mid-request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiRoute, firstRequestMade, customParams, value]
  );

  // Fetch on mount when requestOnMount is true
  useEffect(() => {
    if (requestOnMount) {
      getOptions({
        onStart: () => setLoadingField("onMount", true),
        onSuccess: () => setLoadingField("onMount", false),
        onFailed: () => setLoadingField("onMount", false)
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestOnMount]);

  const onVisibleChange = useCallback(
    (visible) => {
      onDropdownVisibleChange?.(visible);
      if (!firstRequestMade || (visible && options.length === 0)) {
        getOptions({
          reqParams: params,
          onStart: () => setLoadingField("initial", true),
          onSuccess: (data, pager) => {
            setLoadingField("initial", false);
            setParams({ ...params, page: pager.current, total: pager.totalItems });
          },
          onFailed: () => setLoadingField("initial", false)
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options, params, getOptions, firstRequestMade]
  );

  const onScroll = useCallback(
    (event) => {
      const { target } = event;
      const isLastPage = params.per_page * params.page >= params.total;
      if (isLastPage || !params.page) return;

      const distanceToBottom = target.scrollHeight - target.scrollTop - target.offsetHeight;

      if (!loading.initial && !loading.lazy && distanceToBottom <= SCROLL_BOTTOM_THRESHOLD) {
        // Spreading the current params keeps an active search applied while paging through
        // its results, instead of appending the next page of the unfiltered list.
        const reqParams = { ...params, page: params.page + 1 };
        getOptions({
          reqParams,
          onStart: () => setLoadingField("lazy", true),
          onSuccess: (data, pager) => {
            setLoadingField("lazy", false);
            setParams({ ...reqParams, page: pager.current, total: pager.totalItems });
          },
          onFailed: () => setLoadingField("lazy", false)
        });
      }
    },
    [loading, params, getOptions]
  );

  const onSearch = useCallback(
    (searchVal) => {
      if (!serverSearch) return;

      const searchValue = (searchVal || "").trim();

      // antd also fires this with an empty value when the box is cleared or the dropdown
      // closes; only go back to the API if there was an active search to reset.
      if (!searchValue && !activeSearchRef.current) return;
      activeSearchRef.current = searchValue;

      // Columns are sent when the caller knows them; otherwise the API resolves the
      // searchable columns of the queried table itself (searchFallback).
      const searchParams = (() => {
        if (!searchValue) return {};
        if (searchColumns?.length) return { searchValue, searchColumns };
        return { searchValue, searchFallback: 1 };
      })();
      const reqParams = { per_page: params.per_page, page: 1, ...searchParams };

      getOptions({
        reqParams,
        // Results replace the list rather than pile up on top of the previously loaded pages.
        removeExistingOptions: true,
        onStart: () => setLoading((prev) => ({ ...prev, lazy: false, initial: true })),
        onSuccess: (data, pager) => {
          setLoadingField("initial", false);
          setParams({ ...reqParams, page: pager.current, total: pager.totalItems });
        },
        onFailed: () => setLoadingField("initial", false)
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params.per_page, getOptions, searchColumns, serverSearch]
  );

  const debouncedOnSearch = useMemo(() => debounce(onSearch, 300), [onSearch]);

  return { options, loading, serverSearch, onVisibleChange, onScroll, debouncedOnSearch };
};
